import {
  Controller,
  useFormContext,
  useWatch,
  useFieldArray,
} from 'react-hook-form';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { Delete } from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { Card, CardContent } from 'further-ui/components/Card';
import {
  LpAccrualFrequency,
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
  LpFeeType,
} from 'further-types/lp/lp-fee-template';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { InferredFeeSettingsFormType } from './FormWrapper';
import { emptyFee } from './helpers';
import {
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import NumberInput from 'further-ui/components/NumberInput';
import BasicSelect from 'further-ui/components/BasicSelect';
import Table, { Column } from 'further-ui/components/Table';
import { Row } from 'further-ui/components/Layout';
import PageSection from 'components/new/PageSection';

const TextContainer = styled('div')({
  height: '29px',
  // align with other elements without border
  padding: '9px 12px 9px 0px',
  borderRadius: '4px',
});

const feeTypeOptions = [
  {
    label: 'One-off fixed',
    value: LpFeeType.OneOffFixed,
  },
  {
    label: 'Recurring %',
    value: LpFeeType.RecurringPercentage,
  },
];

const calculationBasisOptions = [
  {
    label:
      LpFeeCalculationBasisLabels.get(LpFeeCalculationBasis.CommittedCapital) ??
      '',
    value: LpFeeCalculationBasis.CommittedCapital,
  },
  {
    label:
      LpFeeCalculationBasisLabels.get(
        LpFeeCalculationBasis.CommittedCapitalMinusExcludedInvestments,
      ) ?? '',
    value: LpFeeCalculationBasis.CommittedCapitalMinusExcludedInvestments,
  },
];

const accrualFrequencyOptions = [
  {
    label: 'Quarterly advance',
    value: LpAccrualFrequency.QuarterlyAdvance,
  },
  {
    label: 'Monthly advance',
    value: LpAccrualFrequency.MonthlyAdvance,
  },
  {
    label: 'Bi-annually advance',
    value: LpAccrualFrequency.BiannualyAdvance,
  },
  {
    label: 'Annually advance',
    value: LpAccrualFrequency.AnnuallyAdvance,
  },
  {
    label: 'Quarterly arrears',
    value: LpAccrualFrequency.QuarterlyArrears,
  },
  {
    label: 'Monthly arrears',
    value: LpAccrualFrequency.MonthlyArrears,
  },
  {
    label: 'Bi-annually arrears',
    value: LpAccrualFrequency.BiannualyArrears,
  },
  {
    label: 'Annually arrears',
    value: LpAccrualFrequency.AnnuallyArrears,
  },
];

const commitmentStatusOptions = [
  {
    label:
      LpFeeCommitmentStatusLabels.get(LpFeeCommitmentStatus.InsideCommitment) ??
      '',
    value: LpFeeCommitmentStatus.InsideCommitment,
  },
  {
    label:
      LpFeeCommitmentStatusLabels.get(
        LpFeeCommitmentStatus.OutsideCommitment,
      ) ?? '',
    value: LpFeeCommitmentStatus.OutsideCommitment,
  },
];

const startPeriodTooltipText = (
  <>
    <p>
      A period is defined by the selected <i>Billing frequency</i>. If
      "quarterly" is selected, period 1 is quarter 1.
    </p>
  </>
);

const endPeriodTooltipText = (
  <>
    <p>
      A period is defined by the selected <i>Billing frequency</i>. If
      "quarterly" is selected, period 1 is quarter 1.
    </p>
  </>
);

const feeExTaxTooltipText = (
  <>
    <p>
      Where the fee is recurring, enter the annual fee, not the pro-rata fee
      relevant to the selected <i>Billing frequency</i> period.
    </p>
  </>
);

type LpFee =
  InferredFeeSettingsFormType['lpFeeTemplates'][number]['lpFeeTemplateFees'][number];

type LpFeeRow = LpFee & { id: string };

const FeeTemplateFeesTable: React.FC<{
  index: number;
  hasApprovedFees: boolean;
  templateInUseMessage?: string;
  hasCommitments: boolean;
  setHasEditedTemplateWithCommitment: (
    hasEditedTemplateWithCommitment: boolean,
  ) => void;
}> = ({
  index,
  templateInUseMessage,
  hasApprovedFees,
  hasCommitments,
  setHasEditedTemplateWithCommitment,
}) => {
  const { vehicle } = useVehicle();
  const {
    control,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext<InferredFeeSettingsFormType>();

  const {
    fields: feeFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `lpFeeTemplates.${index}.lpFeeTemplateFees`,
  });

  const fees = useWatch({
    control,
    name: `lpFeeTemplates.${index}.lpFeeTemplateFees`,
  });

  const calculateFeeIncTax = (feeExTax: number, salesTaxRate: number) => {
    return feeExTax * (1 + salesTaxRate / 100);
  };

  const columns: Array<Column<LpFeeRow>> = [
    {
      label: 'Fee name',
      key: 'name',
      width: 250,
      render: (_, i) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.name`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <TextField
                {...field}
                placeholder="Enter fee name"
                fullWidth
                error={!!error}
                variant="outlined"
                size="small"
                disabled={hasApprovedFees}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee type',
      key: 'type',
      width: 250,
      render: (_, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.type`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <span>
                <BasicSelect
                  value={field.value}
                  options={feeTypeOptions}
                  size="small"
                  fullWidth
                  onChange={(value) => {
                    if (value === LpFeeType.RecurringPercentage) {
                      resetField(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.accrualFrequency`,
                      );
                      resetField(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.calculationBasis`,
                      );
                      setValue(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.startPeriod`,
                        0,
                      );
                      setValue(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.endPeriod`,
                        0,
                      );
                    }

                    if (value === LpFeeType.OneOffFixed) {
                      resetField(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.accrualFrequency`,
                      );
                      resetField(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.calculationBasis`,
                      );
                      setValue(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.startPeriod`,
                        0,
                      );
                      setValue(
                        `lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.endPeriod`,
                        0,
                      );
                    }

                    field.onChange(value);
                  }}
                  error={!!error}
                  helperText={error?.message}
                  disabled={hasApprovedFees}
                />
              </span>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee basis',
      key: 'calculationBasis',
      width: 240,
      render: (row: LpFeeRow, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.calculationBasis`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {row.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage}>
                  <span>
                    <BasicSelect
                      value={field.value}
                      fullWidth
                      options={calculationBasisOptions}
                      onChange={field.onChange}
                      error={!!error}
                      helperText={error?.message}
                      disabled={hasApprovedFees}
                    />
                  </span>
                </Tooltip>
              ) : (
                <TextContainer>
                  {LpFeeCalculationBasisLabels.get(LpFeeCalculationBasis.Fixed)}
                </TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Billing frequency',
      key: 'accrualFrequency',
      width: 250,
      render: (row: LpFeeRow, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.accrualFrequency`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {row.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage}>
                  <span>
                    <BasicSelect
                      value={field.value}
                      fullWidth
                      options={accrualFrequencyOptions}
                      onChange={field.onChange}
                      error={!!error}
                      disabled={hasApprovedFees}
                    />
                  </span>
                </Tooltip>
              ) : (
                <TextContainer>N/A</TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Inside/outside commitment',
      key: 'commitmentStatus',
      width: 240,
      render: (_, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.commitmentStatus`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <span>
                <BasicSelect
                  size="small"
                  fullWidth
                  value={field.value}
                  options={commitmentStatusOptions}
                  onChange={field.onChange}
                  disabled={hasApprovedFees}
                  error={!!error}
                />
              </span>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Start period',
      key: 'startPeriod',
      tooltip: startPeriodTooltipText,
      width: 150,
      render: (row: LpFeeRow, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.startPeriod`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {row.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage ?? ''}>
                  <span>
                    <NumberInput
                      {...field}
                      placeholder="e.g. 1"
                      fullWidth
                      disabled={hasApprovedFees}
                      error={!!error}
                    />
                  </span>
                </Tooltip>
              ) : (
                <TextContainer>N/A</TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'End period',
      key: 'finishingPeriod',
      tooltip: endPeriodTooltipText,
      width: 150,
      render: (row: LpFeeRow, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.endPeriod`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {row.type !== LpFeeType.OneOffFixed ? (
                <Tooltip title={templateInUseMessage ?? ''}>
                  <span>
                    <NumberInput
                      {...field}
                      placeholder="e.g. 1"
                      fullWidth
                      error={!!error}
                      disabled={hasApprovedFees}
                    />
                  </span>
                </Tooltip>
              ) : (
                <TextContainer>N/A</TextContainer>
              )}
            </>
          )}
        />
      ),
    },
    {
      label: 'Fee (ex tax)',
      key: 'netFee',
      tooltip: feeExTaxTooltipText,
      width: 150,
      render: (row: LpFeeRow, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.feeExTax`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage ?? ''}>
              <span>
                <NumberInput
                  {...field}
                  decimals={2}
                  maxDecimals={10}
                  error={!!error}
                  disabled={hasApprovedFees}
                  fullWidth
                  currencyStartAdornment={row?.type === LpFeeType.OneOffFixed}
                  currency={vehicle?.baseCurrency}
                  showPercentSuffix={
                    row?.type === LpFeeType.RecurringPercentage
                  }
                />
              </span>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Sales tax rate',
      key: 'salesTaxRate',
      width: 140,
      render: (_, i: number) => (
        <Controller
          name={`lpFeeTemplates.${index}.lpFeeTemplateFees.${i}.salesTaxRate`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={templateInUseMessage}>
              <span>
                <NumberInput
                  {...field}
                  disabled={hasApprovedFees}
                  decimals={2}
                  maxDecimals={10}
                  fixedDecimalScale
                  error={!!error}
                  fullWidth
                  showPercentSuffix
                />
              </span>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee (inc tax)',
      key: 'feeIncTax',
      width: 150,
      render: (row: LpFeeRow) => {
        const feeIncTax = calculateFeeIncTax(row.feeExTax, row.salesTaxRate);
        return (
          <Tooltip title={templateInUseMessage}>
            <>
              {row?.type === LpFeeType.OneOffFixed
                ? numberToCurrencyString(feeIncTax)
                : numberToPercentageString(feeIncTax, {
                    forceDecimals: true,
                    accuracy: 2,
                    noMultiply: true,
                  })}
            </>
          </Tooltip>
        );
      },
    },
    {
      label: 'Action',
      key: 'action',
      width: 80,
      render: (_, i: number) => (
        <Tooltip
          title={
            hasApprovedFees
              ? 'It is not possible to delete a fee template that is currently in use.'
              : ''
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => {
                remove(i);
                setHasEditedTemplateWithCommitment(hasCommitments);
              }}
              disabled={hasApprovedFees}
            >
              <Delete color={hasApprovedFees ? 'disabled' : undefined} />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const feesWithId: Array<LpFeeRow> = feeFields.map((field, i) => ({
    ...(fees[i] ?? field),
    id: field.id,
  }));

  return (
    <Card variant="outlined">
      <CardContent size="sm">
        <PageSection
          withoutBottomDivider
          title={
            <Row fullWidth centered justify="space-between">
              <Typography variant="inherit">Fees</Typography>
              <Tooltip
                title={
                  hasApprovedFees
                    ? 'It is not possible to add a fee to a fee template that is currently in use.'
                    : ''
                }
              >
                <span>
                  <Button
                    size="large"
                    onClick={() => {
                      append({
                        ...emptyFee,
                      });
                      setHasEditedTemplateWithCommitment(hasCommitments);
                    }}
                    color="primary"
                    variant="outlined"
                    disabled={hasApprovedFees}
                  >
                    Add fee
                  </Button>
                </span>
              </Tooltip>
            </Row>
          }
        >
          <Table<LpFeeRow, string>
            columns={columns}
            rows={feesWithId}
            scrollable
          />
          {errors.lpFeeTemplates?.[index]?.lpFeeTemplateFees && (
            <FormHelperText error>
              Please fill all the required fields.
            </FormHelperText>
          )}
        </PageSection>
      </CardContent>
    </Card>
  );
};

export default FeeTemplateFeesTable;
